import React, { Component, Fragment } from "react";
import DispatchProps from "../../utils/DispatchProps";
import PageTitle from "../PageTitle";
import { connect } from "react-redux";
import { IRootState } from "../../store";
import ApiService from "../ApiService";
import * as H from "history";
import * as _ from 'lodash';
import {
    Client as GraphClient,
} from "@microsoft/microsoft-graph-client";
import Table, { TableColumn } from "../Table";
import Actions from "../../redux/Actions";
import { ActionButton, CommandBarButton, DefaultButton, Dialog, DialogFooter, DialogType, Icon, MessageBarType, PrimaryButton } from "office-ui-fabric-react";
import TableCell from "../TableCell";
import TenantManager from "../../models/TenantManager";
import { Link } from "react-router-dom";
import SchoolMenu from "../SchoolMenu";

interface GraphUser {
    displayName: string;
    userPrincipalName: string;
}

interface TenantManagerPageProps {
    tenantId: string;

    history: H.History,
    user: any,

    graphClient: GraphClient;
    apiService: ApiService;

    userIsCdbEmployee: boolean;
}

interface TenantManagerPageState {
    loading?: boolean;
    showDeleteDialog: boolean;

    records: TenantManager[];
    recordToDelete?: TenantManager;

    userDict: { [key: string]: GraphUser };
}

class TenantManagerPage extends Component<TenantManagerPageProps & DispatchProps, TenantManagerPageState> {
    static displayName = TenantManagerPage.name;
    
    public static buildHelpBox(): JSX.Element {
        return (
            <div className={"flex-row flex--justify-content-center p-top-1rem"}>
                <div className="help-box-item help-box-item--wide">
                    <div className="help-box-item__icon">
                        <Icon iconName="Signin" className="" />
                    </div>
                    <span className="help-box-item__title">Manage <strong>Tenant Access</strong></span>
                    <span className="help-box-item__description">
                        <span>Tenant Managers will have access to <strong>all schools</strong> and <strong>all future schools</strong></span>
                        <br />
                        <br />
                        <span>They will also be able to <strong>grant the same access to other users.</strong></span>
                    </span>
                </div>
            </div>
        );
    }

    private static columns: TableColumn[] = [
        {
            title: "User",
            propertyPath: "userOid",
            flex: 1
        },
        {
            title: "",
            propertyPath: "delete",
            flex: 1
        }
    ]; 

    constructor(props: TenantManagerPageProps & DispatchProps) {
        super(props);

        this.state = {
            showDeleteDialog: false,
            records: [],
            userDict: {}
        }
    }

    async componentDidMount(): Promise<void> {
        if (this.props.tenantId) {
            await this.fetchRecords();
        }
    }

    async componentDidUpdate(prevProps: Readonly<TenantManagerPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.tenantId !== prevProps.tenantId) {
            await this.fetchRecords();
        }
    }

    private async fetchRecords() {
        this.setState({ loading: true });

        try {
            const records = await this.props.apiService.getTenantManagersForTenant(this.props.tenantId);
            await this.getUsersByOid(records.filter(r => r.userOid !== undefined).map(r => r.userOid!));
            this.setState({ records: records });
        }
        catch (e) {
            this.props.dispatch(Actions.showMessageBar(`Error getting tenant manager records: ${e.message}`, MessageBarType.error));
        }

        this.setState({ loading: false });
    }

    private async getUsersByOid(userOids: string[]) {
        const userDict: { [key: string]: GraphUser } = {};
        const userOidChunks = _.chunk(userOids, 15);

        for (const userOidChunk of userOidChunks) {
            const filterConditions = userOidChunk.map(userOid => `id eq '${userOid}'`);
            const filter = _.join(filterConditions, ' or ');

            let result: any;

            if (this.props.userIsCdbEmployee) {
                result = await this.props.apiService.searchGraphUsersAdmin(this.props.tenantId, filter);
            }
            else {
                const search = await this.props.graphClient.api("/users").filter(filter).get();
                result = search.value;
            }

            const users = _.keyBy(result, "id");

            Object.assign(userDict, users);
        }

        this.setState({ userDict });
    }

    private async deleteTenantManagerRecord() {
        if (this.state.recordToDelete) {
            await this.props.apiService.deleteTenantManager(this.state.recordToDelete);

            const deletingSelf = this.props.user?.localAccountId === this.state.recordToDelete.userOid;

            this.setState({
                showDeleteDialog: false,
                recordToDelete: undefined
            });

            if (deletingSelf) {
                this.props.history.push("/home");
                window.location.reload();
            }
            else {
                await this.fetchRecords();
            }
        }
    }

    private cellForColumn = (item: TenantManager, column: TableColumn, index: number) => {
        switch (column.propertyPath) {
            case "userOid":
                if (item.userOid !== undefined && this.state.userDict[item.userOid] !== undefined) {
                    return (
                        <TableCell key={index} flex={column.flex}>
                            <span>{this.state.userDict[item.userOid!].displayName}</span>
                            <span className={"cdb-table__extra-detail"}>{this.state.userDict[item.userOid!].userPrincipalName}</span>
                        </TableCell>
                    );
                }
                else {
                    return (
                        <TableCell key={index} flex={column.flex}>
                            <span>{item.userOid}</span>
                        </TableCell>
                    );
                }
            case "delete":
                return (
                    <ActionButton iconProps={{ iconName: "Delete" }} allowDisabledFocus disabled={false} checked={false} onClick={() => {
                            this.setState({
                                showDeleteDialog: true,
                                recordToDelete: item
                            });
                        }}>
                        Remove
                    </ActionButton>
                );
            default:
                return undefined;
        }
    };

    render() {
        return (
            <Fragment>
                <PageTitle title={`Tenant Manager`} />

                <SchoolMenu prefix="Tenant Manager" />

                {TenantManagerPage.buildHelpBox()}

                <Table
                    title={"Tenant Managers"}
                    items={this.state.records}
                    columns={TenantManagerPage.columns}
                    cellForColumn={this.cellForColumn}
                    loading={this.state.loading}>

                    <Link to={`/manage-tenant/${this.props.tenantId}/tenant-manager/add`}>
                        <CommandBarButton
                            iconProps={{ iconName: "add" }}
                            text="Add"
                            onClick={() => { }}
                        />
                    </Link>
                </Table>

                <Dialog
                    hidden={!this.state.showDeleteDialog}
                    onDismiss={() => {
                        this.setState({
                            showDeleteDialog: false
                        });
                    }}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Remove Access',
                        subText: `Do you want to remove access for ${this.state.recordToDelete?.userOid}?`
                    }}
                    modalProps={{
                        isBlocking: false,
                        styles: { main: { maxWidth: 900 } },
                    }}>
                    <DialogFooter>
                        <PrimaryButton onClick={async () => {
                            await this.deleteTenantManagerRecord();
                        }} text="Remove" />
                        <DefaultButton onClick={() => {
                            this.setState({
                                showDeleteDialog: false,
                                recordToDelete: undefined,
                            });
                        }} text="Cancel" />
                    </DialogFooter>
                </Dialog>

            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): TenantManagerPageProps => {
        return {
            tenantId: ownProps.match.params.tenantId,

            history: ownProps.history,
            user: state.auth.user!,

            graphClient: state.auth.graphClient,
            apiService: state.auth.apiService,

            userIsCdbEmployee: state.auth.userIsCdbEmployee
        };
    }
)(TenantManagerPage)